import { useCallback, useRef, useState } from 'react';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import { api } from '../api';
import { MAX_FILE_SIZE_BYTES } from '../core/types';
import { ensurePdfWorkerConfigured } from '../core/render';
import type { RunState } from '../App';

interface Props {
  onStarted: (state: RunState) => void;
}

type Phase = 'idle' | 'parsing' | 'uploading' | 'starting' | 'queued' | 'error';

export default function UploadPage({ onStarted }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [pageCount, setPageCount] = useState<number | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [localMode, setLocalMode] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback(async (f: File): Promise<number | null> => {
    if (!f.name.toLowerCase().endsWith('.pdf')) {
      setError('仅支持 .pdf 文件');
      return null;
    }
    if (f.size === 0) {
      setError('文件不能为空');
      return null;
    }
    if (f.size > MAX_FILE_SIZE_BYTES) {
      setError(`文件超过 100 MB 上限（当前 ${(f.size / 1024 / 1024).toFixed(1)} MB）`);
      return null;
    }
    setError(null);
    setPhase('parsing');
    try {
      ensurePdfWorkerConfigured();
      console.log('[validate] start, workerSrc =', import.meta.env.DEV ? '' : GlobalWorkerOptions?.workerSrc);
      const buf = await f.arrayBuffer();
      console.log('[validate] arrayBuffer done', buf.byteLength);
      const doc = await withTimeout(
        getDocument({ data: buf }).promise,
        30000,
        'PDF 解析超时，请重试',
      );
      console.log('[validate] parsed pages =', doc.numPages);
      const count = doc.numPages;
      await doc.destroy();
      console.log('[validate] ok');
      if (count < 1) {
        setError('PDF 未包含任何页面');
        return null;
      }
      setPhase('idle');
      return count;
    } catch (e) {
      console.log('[validate] ERROR:', e instanceof Error ? e.message : String(e));
      setError(`无法解析该 PDF，文件可能已损坏或不是有效的 PDF（${e instanceof Error ? e.message : String(e)}）`);
      setPhase('idle');
      return null;
    }
  }, []);

  const onFile = useCallback(
    async (f: File | undefined | null) => {
      if (!f) return;
      setFile(f);
      setPageCount(null);
      const count = await validateFile(f);
      setPageCount(count);
    },
    [validateFile],
  );

  function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timer = window.setTimeout(() => reject(new Error(message)), ms);
      promise.then(
        (v) => {
          window.clearTimeout(timer);
          resolve(v);
        },
        (e) => {
          window.clearTimeout(timer);
          reject(e);
        },
      );
    });
  }

  const start = async () => {
    if (!file || pageCount === null) return;
    setPhase('uploading');
    setError(null);
    setProgress(0);
    try {
      const sourceBytes = await file.arrayBuffer();
      if (localMode) {
        // Local mode: everything happens in the browser, nothing is uploaded.
        setPhase('idle');
        onStarted({ sourceBytes, sourceFile: file, job: null });
        return;
      }
      const created = await api.createJob({ fileName: file.name, sizeBytes: file.size });
      // Upload with progress (the local server and Worker both accept PUT).
      await api.uploadSourceWithProgress(created.uploadUrl, new Uint8Array(sourceBytes), setProgress);
      setProgress(1);
      setPhase('starting');
      const job = await api.startJob(created.jobId);
      setPhase(job.status === 'queued' ? 'queued' : 'idle');
      onStarted({
        sourceBytes,
        sourceFile: file,
        job,
      });
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setError(message);
      setPhase('error');
    }
  };

  const sizeLabel = (n: number) =>
    n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(2)} MB` : `${(n / 1024).toFixed(1)} KB`;

  return (
    <div className="page">
      <header className="hero">
        <h1>SPS Commerce Label PDF 拆分</h1>
        <p>上传 ASN 导出的多订单 shipping label PDF，自动识别 Tracking Number 并按订单类型拆分</p>
      </header>

      <div
        className={`dropzone ${dragOver ? 'drag-over' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          void onFile(e.dataTransfer.files?.[0]);
        }}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          hidden
          onChange={(e) => void onFile(e.target.files?.[0])}
        />
        {file ? (
          <div className="file-card" onClick={(e) => e.stopPropagation()}>
            <div className="file-name">{file.name}</div>
            <div className="file-meta">
              {sizeLabel(file.size)}
              {pageCount !== null && ` · ${pageCount} 页`}
            </div>
            {phase === 'parsing' && <div className="hint">正在校验 PDF…</div>}
            <button
              className="ghost"
              onClick={(e) => {
                e.stopPropagation();
                setFile(null);
                setPageCount(null);
                setError(null);
                setPhase('idle');
              }}
            >
              移除并重新选择
            </button>
          </div>
        ) : (
          <>
            <div className="dropzone-icon">PDF</div>
            <p>
              拖拽 PDF 到此处，或 <span className="link">点击选择文件</span>
            </p>
            <p className="hint">仅支持 .pdf · 单个文件不超过 100 MB · 不设页数上限</p>
          </>
        )}
      </div>

      {error && <div className="alert error">{error}</div>}

      {phase === 'uploading' && (
        <div className="progress-wrap">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress * 100}%` }} />
          </div>
          <span>正在上传源文件…</span>
        </div>
      )}
      {phase === 'queued' && <div className="alert info">已进入处理队列，等待空闲处理槽…</div>}

      <label className="mode-toggle">
        <input
          type="checkbox"
          checked={localMode}
          onChange={(e) => setLocalMode(e.target.checked)}
        />
        本地处理（不上传，直接下载结果）
      </label>

      <div className="actions">
        <button className="primary" disabled={!file || pageCount === null || phase === 'parsing' || phase === 'uploading' || phase === 'starting'} onClick={() => void start()}>
          {phase === 'uploading' ? '上传中…' : phase === 'starting' ? '启动中…' : '开始拆分'}
        </button>
      </div>

      <section className="notes">
        <h2>处理规则</h2>
        <ul>
          <li>单品单件订单的 label 页合并到 <code>源文件名_single.pdf</code></li>
          <li>多包裹 / 多品订单按 PO Number 拆分为独立 PDF（文件名以 PO 命名，一个 PDF 含多张 label）</li>
          <li>无法识别的页面保留到 <code>unresolved-pages.pdf</code>，不丢页、不改顺序</li>
          <li>
            默认本地模式：文件不会上传，OCR 与拆分在浏览器内完成，结果直接下载；也可取消勾选上传到云端保存记录
          </li>
        </ul>
      </section>
    </div>
  );
}
